import {
  Component,
  ViewChild,
  AfterViewInit,
  ElementRef,
  Renderer2,
  Input,
  OnInit,
  ChangeDetectorRef,
  RendererStyleFlags2,
  Optional,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { NavController } from '@ionic/angular';
import { BehaviorSubject, Subscription } from 'rxjs';
import { PublicHolidayService } from '../../services/public-holiday.service';
import { TimesheetService } from '../../services/timesheet.service';
import { ExpenseService } from '../../services/expense.service';
import { ControlContainer, FormGroupDirective } from '@angular/forms';

type ISODate = string;

interface TimeMark {
  id?: number;
  hours?: number | string;
  color?: string;
  project_name?: string;
  item_category_name?: string;
  item_state_tech_key?: string;
  isPublicHoliday?: boolean | string;
  text?: string;
  dayColor?: string;
}

interface ExpenseMark {
  id: number;
  item_state_tech_key: string;
  project_name?: string;
  currency?: string;
  quantity?: number | string;
  description?: string;
  item_category_name?: string;
}

interface MarkedEntry {
  time: TimeMark[];
  other: any[];
  expense: ExpenseMark[];
}

@Component({
  selector: 'app-custom-ion-datetime',
  templateUrl: './custom-ion-datetime.component.html',
  styleUrls: ['./custom-ion-datetime.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, FormGroupDirective]],
      useFactory: (formGroupDirective: FormGroupDirective) => formGroupDirective,
    },
  ],
  standalone: false,
})
export class CustomIonDatetimeComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() marked: any;
  @Input() includePublicHolidays: boolean;
  @Input() includeTimes: boolean;
  @Input() includeExpenses: boolean;
  @Input() includeDetails: boolean;
  @Input() includeLegend: boolean = true;
  @Input() color: string;
  @Input() multiple: boolean;
  @Input() max: any;
  @Input() highlightedDates: any;
  @Input() firstDayOfWeek: any;
  @Input() showDefaultTitle: any;
  @Input() showClearButton: any;
  @Input() size: any;
  @Input() class: any;
  @Input() value: any;
  @Input() public controlName: string;
  @ViewChild('calendar', { read: ElementRef, static: false })
  calendar?: ElementRef;
  @Input() public dateSelected: any;

  /* ----------  API (for <app-time-expense-details>) ---------- */
  public markedAssociative: { [date: string]: MarkedEntry } = {};

  /* ---------- State ---------- */
  private $dateRange = new BehaviorSubject<[ISODate, ISODate]>([
    `${new Date().getFullYear()}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}-01`,
    `${new Date().getFullYear()}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}-${new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0
    ).getDate()}`,
  ]);

  private times = new BehaviorSubject<any[]>([]);
  private expenses = new BehaviorSubject<any[]>([]);

  private monthGridObserver?: MutationObserver;
  private selectionObserver?: MutationObserver;
  private footerOnceObserver?: MutationObserver;

  private subs: Subscription[] = [];

  /* ---------- Color-Tokens (CSS Vars) ---------- */
  private readonly TIMECOLORS = {
    vacation: 'var(--datetime-vacation-color)',
    delivery: 'var(--datetime-delivery-color)',
    admin: 'var(--datetime-admin-color)',
    sickness: 'var(--datetime-sickness-color)',
    publicHoliday: 'var(--datetime-holiday-color)',
    publicHolidayRegional: 'var(--datetime-holiday-regional-color, pink)',
    other: 'var(--datetime-other-color)',
    default: 'var(--datetime-default-color)',
  } as const;

  private readonly EXPENSECOLORS = {
    draft: 'var(--datetime-expense-draft)',
    submitted: 'var(--datetime-expense-submitted)',
    approved: 'var(--datetime-expense-approved)',
    rejected: 'var(--datetime-expense-rejected)',
    denied: 'var(--datetime-expense-denied)',
    ready: 'var(--datetime-expense-ready)',
    paid: 'var(--datetime-expense-paid)',
  } as const;

  private mapTimeItemCategory = new Map<string, string>([
    ['vacation', this.TIMECOLORS.vacation],
    ['delivery', this.TIMECOLORS.delivery],
    ['admin', this.TIMECOLORS.admin],
    ['sickness', this.TIMECOLORS.sickness],
    ['public_holiday', this.TIMECOLORS.publicHoliday],
    ['public_holiday_regional', this.TIMECOLORS.publicHolidayRegional],
  ]);

  private mapExpenseItemState = new Map<string, [number, string, string]>([
    ['draft', [1, 'checkmark-outline', this.EXPENSECOLORS.draft]],
    ['submitted', [2, 'checkmark-done-outline', this.EXPENSECOLORS.submitted]],
    ['approved', [3, 'checkmark-done-outline', this.EXPENSECOLORS.approved]],
    ['rejected', [4, 'checkmark-outline', this.EXPENSECOLORS.rejected]],
    ['denied', [5, 'checkmark-done-outline', this.EXPENSECOLORS.denied]],
    ['ready', [6, 'checkmark-outline', this.EXPENSECOLORS.ready]],
    ['paid', [7, 'checkmark-done-outline', this.EXPENSECOLORS.paid]],
  ]);

  private mapLegend = new Map<string, string>([
    ['Public Holiday', this.TIMECOLORS.publicHoliday],
    ['Non‑Public Holiday', this.TIMECOLORS.publicHolidayRegional],
    ['Vacation', this.TIMECOLORS.vacation],
    ['Delivery', this.TIMECOLORS.delivery],
    ['Admin', this.TIMECOLORS.admin],
    ['Sickness', this.TIMECOLORS.sickness],
    ['Other', this.TIMECOLORS.other],
  ]);

  constructor(
    private renderer: Renderer2,
    private el: ElementRef<HTMLElement>,
    private publicHolidayService: PublicHolidayService,
    private timesheetService: TimesheetService,
    private expenseService: ExpenseService,
    private navController: NavController,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    @Optional() private controlContainer: ControlContainer
  ) {}

  /* ===================== Lifecycle ===================== */

  ngOnInit(): void {
    if (this.controlName && !this.controlContainer) {
      throw new Error(
        'formControlName must be used with a parent formGroup directive. Please add a formGroup directive to the parent element.'
      );
    }
    this.initializeMarkedAssociative();
    this.setupDataSubscriptions();
  }

  async ngAfterViewInit() {
    const shadow = await this.waitForShadowRoot();
    if (!shadow) return;

    // NEW: ensure all parts are present so your ::part(...) CSS takes effect
    this.applyBaseParts(shadow); // <<<<<<<<<<<<<<<<<<<<< NEW

    this.zone.runOutsideAngular(() => {
      const monthGrid = shadow.querySelector('.calendar-month-grid');
      if (monthGrid) {
        this.monthGridObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            // NEW: refresh parts on re-render (month slide)
            this.applyBaseParts(shadow); // <<<<<<<<<<<<<<<<<<<<< NEW
            this.resetAndRestyleVisible(shadow);
            this.syncVisibleDateRange(shadow);
          });
        });
        this.monthGridObserver.observe(monthGrid, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-hidden'],
        });
      }

      this.selectionObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && (m.target as Element).classList.contains('calendar-day')) {
            requestAnimationFrame(() =>
              this.styleDayElement((m.target as Element).closest('.calendar-day-wrapper'))
            );
            break;
          }
        }
      });
      this.selectionObserver.observe(shadow, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    });

    this.rebuildComponent(shadow);
    this.syncVisibleDateRange(shadow);
    this.resetAndRestyleVisible(shadow);
  }

  ngOnDestroy(): void {
    this.monthGridObserver?.disconnect();
    this.selectionObserver?.disconnect();
    this.footerOnceObserver?.disconnect();
    this.subs.forEach((s) => s.unsubscribe());
  }

  /* ===================== Daten-Setup ===================== */

  private initializeMarkedAssociative() {
    if (!this.marked) return;
    for (const date in this.marked) {
      const v = this.marked[date];
      this.markedAssociative[date] = {
        time: [...(v.time || [])],
        other: [...(v.other || [])],
        expense: [...(v.expense || [])],
      };
    }
  }

  private setupDataSubscriptions() {
    if (this.includePublicHolidays) {
      this.publicHolidayService.loadAll('all');
      const s = this.publicHolidayService.$publicHolidays.subscribe((holidays) => {
        holidays?.forEach((holiday) => {
          const date = holiday.date as string;
          if (!this.markedAssociative[date]) {
            this.markedAssociative[date] = { time: [], other: [], expense: [] };
          }
          if (!this.markedAssociative[date].time.some((t) => t.text === holiday.name)) {
            this.markedAssociative[date].time.push({
              text: holiday.name,
              hours: '8',
              color: (holiday.subdivision || holiday.type != 'Public') ? this.TIMECOLORS.publicHolidayRegional : this.TIMECOLORS.publicHoliday,
              dayColor: (holiday.subdivision || holiday.type != 'Public') ? this.TIMECOLORS.publicHolidayRegional : this.TIMECOLORS.publicHoliday,
              isPublicHoliday: 'true',
            });
          }
        });
        this.restyleIfVisible();
      });
      this.subs.push(s);
    }

    if (this.includeTimes) {
      this.timesheetService.load('all', this.$dateRange, true);
      const s = this.timesheetService.$calendarEntries.subscribe((times) => {
        this.times.next(times ?? []);
        times?.forEach((time) => {
          const date = time.date as string;
          if (!this.markedAssociative[date]) {
            this.markedAssociative[date] = { time: [], other: [], expense: [] };
          }
          if (!this.markedAssociative[date].time.some((entry) => entry.id === time.id)) {
            this.markedAssociative[date].time.push({
              hours: time.quantity,
              color:
                this.mapTimeItemCategory.get(time.item_category?.tech_key) ??
                this.TIMECOLORS.other,
              id: time.id,
              project_name: time.project?.name,
              item_category_name: time.item_category?.name,
              item_state_tech_key: time.item_state?.tech_key,
            });
          }
        });
        this.restyleIfVisible();
      });
      this.subs.push(s);
    }

    if (this.includeExpenses) {
      const filter = {
        itemCategories: '',
        currencies: '',
        date: [{ id: this.$dateRange.value[0] }, { id: this.$dateRange.value[1] }],
      };
      this.expenseService.loadJournal('all', filter);
      const s = this.expenseService.$journals.subscribe((expenses) => {
        this.expenses.next(expenses ?? []);
        expenses?.forEach((expense) => {
          const date = expense.date as string;
          if (!this.markedAssociative[date]) {
            this.markedAssociative[date] = { time: [], other: [], expense: [] };
          }
          if (!this.markedAssociative[date].expense.some((entry) => entry.id === expense.id)) {
            this.markedAssociative[date].expense.push({
              quantity: expense.quantity,
              currency: expense.currency?.shortcode,
              id: expense.id,
              description: expense.description,
              item_category_name: expense.item_category?.name,
              item_state_tech_key: expense.item_state?.tech_key,
              project_name: expense.project?.name,
            });
          }
        });
        this.restyleIfVisible();
      });
      this.subs.push(s);
    }
  }

  private restyleIfVisible() {
    const shadow = this.getShadowRoot();
    if (!shadow) return;
    requestAnimationFrame(() => this.resetAndRestyleVisible(shadow));
  }

  /* ===================== User Interaction ===================== */

  public onDateChange(_e: any) {
    const dates = Array.isArray(this.dateSelected) ? this.dateSelected : [this.dateSelected];
    dates.sort((a, b) => (a > b ? 1 : -1));
    this.dateSelected = dates;
    const shadow = this.getShadowRoot();
    if (shadow) requestAnimationFrame(() => this.resetAndRestyleVisible(shadow));
  }

  editExpense(id: number) {
    const form = this.expenseService.createExpenseForm();
    this.expenseService.fillExpenseForm(
      form,
      this.expenses.value.find((e) => e.id == id)
    );
    this.expenseService.$editing.next(form);
    this.navController.navigateForward(['/', 'expenses', id]);
  }

  editTime(id: number) {
    const form = this.timesheetService.createTimeForm();
    this.timesheetService.fillTimeForm(
      form,
      this.times.value.find((t) => t.id == id)
    );
    this.timesheetService.$editing.next(form);
    this.navController.navigateForward(['/', 'timesheet', id]);
  }

  /* ===================== Shadow/Styling Core ===================== */

  private async waitForShadowRoot(): Promise<ShadowRoot | null> {
    const host = this.el.nativeElement.getElementsByTagName('ion-datetime')[0] as any;
    if (!host) return null;
    if (typeof host.componentOnReady === 'function') {
      await host.componentOnReady();
    }
    return (host.shadowRoot as ShadowRoot) ?? null;
  }

  private getShadowRoot(): ShadowRoot | null {
    return (this.el.nativeElement.getElementsByTagName('ion-datetime')[0] as any)?.shadowRoot ?? null;
  }

  // NEW: setzt die fehlenden part-Attribute, damit CSS ::part(...) greift (Höhe etc.)
  private applyBaseParts(shadow: ShadowRoot) { // <<<<<<<<<<<<<<<<<<<<< NEW
    shadow.querySelectorAll<HTMLElement>('.calendar-month').forEach(el => {
      if (!el.hasAttribute('part')) el.setAttribute('part', 'calendar-month');
    });
    shadow.querySelectorAll<HTMLElement>('.calendar-month-grid').forEach(el => {
      if (!el.hasAttribute('part')) el.setAttribute('part', 'calendar-month-grid');
    });
    shadow.querySelectorAll<HTMLElement>('.calendar-day-wrapper').forEach(el => {
      if (!el.hasAttribute('part')) el.setAttribute('part', 'calendar-day-wrapper');
    });
    // optional, meist schon vorhanden:
    shadow.querySelectorAll<HTMLElement>('.calendar-day').forEach(el => {
      if (!el.hasAttribute('part')) el.setAttribute('part', 'calendar-day');
    });
  }

  private resetAndRestyleVisible(shadow: ShadowRoot) {
    this.clearAllExpenseIcons(shadow);

    shadow
      .querySelectorAll('.calendar-day-status-container, .calendar-day-subtext')
      .forEach((n) => (n.textContent = ''));

    const visibleMonths = shadow.querySelectorAll('.calendar-month:not([aria-hidden="true"])');
    visibleMonths.forEach((month) => {
      month.querySelectorAll('.calendar-day-wrapper').forEach((wrapper) => {
        const day = wrapper.querySelector('.calendar-day') as HTMLElement | null;
        if (!day) return;

        if (!this.isValidDay(day)) {
          const container = wrapper.querySelector('.calendar-day-container') as HTMLElement | null;
          if (container) {
            const status = container.querySelector('.calendar-day-status-container') as HTMLElement | null;
            if (status) status.textContent = '';
            const sub = container.querySelector('.calendar-day-subtext') as HTMLElement | null;
            if (sub) sub.textContent = '';
          }
          return;
        }

        if (!wrapper.querySelector('.calendar-day-container')) {
          this.rebuildDayElement(wrapper, day);
        }

        this.styleDayElement(wrapper);
      });
    });

    this.cdr.detectChanges();
  }

  private syncVisibleDateRange(shadow: ShadowRoot) {
    const visibleDays = shadow.querySelectorAll('.calendar-day:not([aria-hidden="true"])');
    if (!visibleDays.length) return;

    const start = this.getDateFromElement(visibleDays[0]);
    const end = this.getDateFromElement(visibleDays[visibleDays.length - 1]);
    if (!start || !end) return;

    const cur = this.$dateRange.value;
    if (!cur || cur[0] !== start || cur[1] !== end) {
      this.$dateRange.next([start, end]);
      this.reloadData(); // only Daten, no UI
    }
  }

  private reloadData(): void {
    const range = this.$dateRange.value;
    if (!range) return;
    const [start, end] = range;

    if (this.includePublicHolidays) {
      this.publicHolidayService.loadAll('all');
      const holidays = this.publicHolidayService.$publicHolidays.value ?? [];
      holidays.forEach((holiday) => {
        const date = holiday.date as string;
        if (!this.markedAssociative[date]) {
          this.markedAssociative[date] = { time: [], other: [], expense: [] };
        }
        if (!this.markedAssociative[date].time.some((entry) => entry.text === holiday.name)) {
          this.markedAssociative[date].time.push({
            text: holiday.name,
            hours: '8',
            color: (holiday.subdivision || holiday.type != 'Public') ? this.TIMECOLORS.publicHolidayRegional : this.TIMECOLORS.publicHoliday,
            dayColor: (holiday.subdivision || holiday.type != 'Public') ? this.TIMECOLORS.publicHolidayRegional : this.TIMECOLORS.publicHoliday,
            isPublicHoliday: 'true',
          });
        }
      });
    }

    if (this.includeTimes) {
      this.timesheetService.load('all', this.$dateRange, true);
      const times = this.timesheetService.$calendarEntries.value ?? [];
      times.forEach((time) => {
        const date = time.date as string;
        if (!this.markedAssociative[date]) {
          this.markedAssociative[date] = { time: [], other: [], expense: [] };
        }
        if (!this.markedAssociative[date].time.some((entry) => entry.id === time.id)) {
          this.markedAssociative[date].time.push({
            hours: time.quantity,
            color:
              this.mapTimeItemCategory.get(time.item_category?.tech_key) ??
              this.TIMECOLORS.other,
            id: time.id,
            project_name: time.project?.name,
            item_category_name: time.item_category?.name,
            item_state_tech_key: time.item_state?.tech_key,
          });
        }
      });
    }

    if (this.includeExpenses) {
      const filter = {
        itemCategories: '',
        currencies: '',
        date: [{ id: start }, { id: end }],
      };
      this.expenseService.loadJournal('all', filter);
      const expenses = this.expenseService.$journals.value ?? [];
      expenses.forEach((expense) => {
        const date = expense.date as string;
        if (!this.markedAssociative[date]) {
          this.markedAssociative[date] = { time: [], other: [], expense: [] };
        }
        if (!this.markedAssociative[date].expense.some((entry) => entry.id === expense.id)) {
          this.markedAssociative[date].expense.push({
            quantity: expense.quantity,
            currency: expense.currency?.shortcode,
            id: expense.id,
            description: expense.description,
            item_category_name: expense.item_category?.name,
            item_state_tech_key: expense.item_state?.tech_key,
            project_name: expense.project?.name,
          });
        }
      });
    }
  }

  private getDateFromElement(calendarDay: Element): ISODate | null {
    const year = calendarDay.getAttribute('data-year');
    const month = calendarDay.getAttribute('data-month');
    const day = calendarDay.getAttribute('data-day');
    if (!year || !month || !day) return null;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  private rebuildDayElement(dayWrapper: Element, calendarDay: Element) {
    const container = this.renderer.createElement('div');
    this.renderer.setAttribute(container, 'class', 'calendar-day-container');
    this.renderer.setAttribute(container, 'part', 'calendar-day-container');
    this.renderer.setAttribute(container, 'custom-part', 'calendar-day-container');

    const parent = dayWrapper.parentElement!;
    parent.appendChild(calendarDay);
    while (dayWrapper.firstChild) dayWrapper.removeChild(dayWrapper.firstChild);

    this.renderer.appendChild(container, calendarDay);

    if ((calendarDay as Element).getAttribute('data-day')) {
      const statusContainer = this.createStatusContainer();
      const defaultStatusBar = this.createStatusBar(this.TIMECOLORS.default, 1);
      this.renderer.appendChild(statusContainer, defaultStatusBar);
      this.renderer.appendChild(container, statusContainer);
    }

    const subtext = this.renderer.createElement('div');
    this.renderer.setAttribute(subtext, 'part', 'calendar-day-subtext');
    this.renderer.setAttribute(subtext, 'class', 'calendar-day-subtext');
    this.renderer.appendChild(container, subtext);

    this.renderer.appendChild(dayWrapper, container);
  }

  private createStatusContainer(): HTMLElement {
    const container = this.renderer.createElement('div');
    this.renderer.setAttribute(container, 'part', 'calendar-day-status-container');
    this.renderer.setAttribute(container, 'custom-part', 'calendar-day-status-container');
    this.renderer.setAttribute(container, 'class', 'calendar-day-status-container');
    return container;
  }

  private createStatusBar(color: string, widthFraction: number): HTMLElement {
    const bar = this.renderer.createElement('div');
    this.renderer.setAttribute(bar, 'part', 'calendar-day-status');
    this.renderer.setAttribute(bar, 'custom-part', 'calendar-day-status');
    this.renderer.setAttribute(bar, 'class', 'calendar-day-status');
    this.renderer.setStyle(bar, 'background', color);
    this.renderer.setStyle(bar, 'width', `${Math.max(0, Math.min(1, widthFraction)) * 100}%`);
    return bar;
  }

  private styleDayElement(wrapper: Element | null) {
    if (!wrapper) return;
    const container = wrapper.querySelector('.calendar-day-container') as HTMLElement | null;
    const calendarDay = wrapper.querySelector('.calendar-day') as HTMLElement | null;
    if (!container || !calendarDay) return;

    const date = this.getDateFromElement(calendarDay);
    this.resetDayStyles(calendarDay);
    const statusContainer = container.querySelector('.calendar-day-status-container') as HTMLElement | null;
    if (statusContainer) statusContainer.textContent = '';
    const subtext = container.querySelector('.calendar-day-subtext') as HTMLElement | null;
    if (subtext) subtext.textContent = '';

    if (!date) return;

    const data = this.markedAssociative[date];

    if (statusContainer) {
      if (data?.time?.length) {
        const total = data.time.reduce((sum, t) => sum + Number(t.hours ?? 0), 0) || 1;
        data.time.forEach((t) => {
          const bar = this.createStatusBar(t.color ?? this.TIMECOLORS.default, Number(t.hours ?? 0) / total);
          this.renderer.setStyle(bar, 'background', t.color ?? this.TIMECOLORS.default, RendererStyleFlags2.Important);
          this.renderer.appendChild(statusContainer, bar);
        });
      } else {
        const def = this.createStatusBar(this.TIMECOLORS.default, 1);
        this.renderer.appendChild(statusContainer, def);
      }
    }

    const hol = data?.time?.find((t) => t.isPublicHoliday);
    if (hol && subtext) {
      this.renderer.appendChild(subtext, this.renderer.createText(hol.text ?? ''));
      this.renderer.setStyle(calendarDay, 'color', hol.dayColor ?? this.TIMECOLORS.publicHoliday, RendererStyleFlags2.Important);
    }

    if (data?.expense?.length) {
      let minKey = '';
      let minRank = Number.POSITIVE_INFINITY;
      data.expense.forEach((ex) => {
        const m = ex.item_state_tech_key && this.mapExpenseItemState.get(ex.item_state_tech_key);
        if (m && m[0] < minRank) { minRank = m[0]; minKey = ex.item_state_tech_key; }
      });
      if (minKey) {
        const iconName = this.mapExpenseItemState.get(minKey)![1];
        const iconColor = this.mapExpenseItemState.get(minKey)![2];
        const icon = this.ionIcon(iconName);
        this.renderer.setStyle(icon, 'color', iconColor);
        this.renderer.setStyle(icon, 'font-size', '12px');
        this.renderer.appendChild(calendarDay, icon);
      }
    }

    if (calendarDay.classList.contains('calendar-day-active')) {
      this.renderer.setStyle(calendarDay, 'color', 'purple', RendererStyleFlags2.Important);
    } else if (!hol && this.isWeekend(calendarDay)) {
      this.renderer.setStyle(calendarDay, 'color', 'var(--datetime-weekend-color)', RendererStyleFlags2.Important);
    }
  }

  private resetDayStyles(calendarDay: Element) {
    this.renderer.setStyle(calendarDay, 'background', 'none', RendererStyleFlags2.Important);
    this.renderer.setStyle(calendarDay, 'box-shadow', 'none', RendererStyleFlags2.Important);
    calendarDay.querySelectorAll('ion-icon.me-expense-icon').forEach((i) => i.remove());
  }

  private ionIcon(name: string): HTMLElement {
    const icon = this.renderer.createElement('ion-icon');
    this.renderer.setAttribute(icon, 'part', 'ion-icon');
    this.renderer.setAttribute(icon, 'custom-part', 'ion-icon');
    this.renderer.setAttribute(icon, 'class', 'ion-icon me-expense-icon');
    this.renderer.setAttribute(icon, 'name', name);
    return icon;
  }

  private clearAllExpenseIcons(shadow: ShadowRoot) {
    shadow.querySelectorAll('.calendar-day ion-icon.me-expense-icon').forEach((icon) => icon.remove());
  }

  private isWeekend(calendarDay: Element): boolean {
    const year = parseInt(calendarDay.getAttribute('data-year') || '0', 10);
    const month = parseInt(calendarDay.getAttribute('data-month') || '0', 10);
    const day = parseInt(calendarDay.getAttribute('data-day') || '0', 10);
    const date = new Date(year, month - 1, day);
    return date.getDay() === 0 || date.getDay() === 6;
  }

  private isValidDay(element: Element): boolean {
    return !!(
      element.getAttribute('data-day') &&
      element.getAttribute('data-month') &&
      element.getAttribute('data-year')
    );
  }

  /* ===================== Footer/Legend Re-Insert ===================== */

  private rebuildComponent(shadow: ShadowRoot) {
    const host = this.el.nativeElement;

    const tryMoveDetailsAndLegend = () => {
      const details = host.querySelector('.calendar-details-span');
      const footer = shadow.querySelector('.datetime-footer');
      if (!details || !footer) return false;
      if (details.parentElement === footer) return true;

      footer.insertBefore(details, footer.firstChild);
      if (this.includeLegend) this.addLegend(shadow);
      return true;
    };

    if (tryMoveDetailsAndLegend()) return;

    this.footerOnceObserver = new MutationObserver(() => {
      if (tryMoveDetailsAndLegend()) {
        this.footerOnceObserver?.disconnect();
      }
    });
    this.footerOnceObserver.observe(shadow, { childList: true, subtree: true });
  }

  private addLegend(shadow: ShadowRoot) {
    const footer = shadow.querySelector('.datetime-footer');
    if (!footer) return;

    footer.querySelector('.calendar-legend')?.remove();

    const legend = this.renderer.createElement('div');
    this.renderer.setAttribute(legend, 'class', 'calendar-legend');
    this.renderer.setAttribute(legend, 'part', 'calendar-legend');

    this.mapLegend.forEach((color, label) => {
      const item = this.createLegendItem(color, label);
      this.renderer.appendChild(legend, item);
    });

    footer.insertBefore(legend, footer.firstChild);
  }

  private createLegendItem(color: string, label: string): HTMLElement {
    const item = this.renderer.createElement('div');
    this.renderer.setAttribute(item, 'class', 'calendar-legend-entry');
    this.renderer.setStyle(item, 'display', 'flex');
    this.renderer.setStyle(item, 'align-items', 'center');
    this.renderer.setStyle(item, 'gap', '4px');

    const colorBox = this.renderer.createElement('div');
    this.renderer.setAttribute(colorBox, 'class', 'calendar-legend-entry-status');
    this.renderer.setStyle(colorBox, 'width', '0.45em');
    this.renderer.setStyle(colorBox, 'height', '0.45em');
    this.renderer.setStyle(colorBox, 'border-radius', '50%');
    this.renderer.setStyle(colorBox, 'background', color);

    const text = this.renderer.createElement('span');
    this.renderer.setAttribute(text, 'class', 'calendar-legend-entry-label');
    this.renderer.setStyle(text, 'font-size', '0.7em');
    this.renderer.appendChild(text, this.renderer.createText(label));

    this.renderer.appendChild(item, colorBox);
    this.renderer.appendChild(item, text);
    return item;
  }
}
